import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ApiService from '../services/api.service';

export interface UserConfig {
  frontegg_id: string;
  current_branch?: string | null;
  dbt_schema_prefix?: string | null;
  has_github_access?: boolean;
  date_dimension_variant?: string | null;
  favorite_node_ids?: string[];
  favorite_analysis_ids?: string[];
  favorite_dashboard_ids?: string[];
  is_internal_staff?: boolean;
  stack?: string;
}

const USER_CONFIG_QUERY_KEY = ['userConfig'];

export function useUserConfig() {
  return useQuery<UserConfig>({
    queryKey: USER_CONFIG_QUERY_KEY,
    queryFn: async () => {
      const data = await ApiService.getInstance().request('GET', '/user-config');
      return data as UserConfig;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    retry: 1,
  });
}

const FAVORITE_FIELDS = {
  analysis: 'favorite_analysis_ids',
  dashboard: 'favorite_dashboard_ids',
  node: 'favorite_node_ids',
} as const;

export type FavoriteKind = keyof typeof FAVORITE_FIELDS;

/**
 * Add/remove an item from the current user's favorites.
 *
 * Favorites live on the User document (same lists the old frontend writes via
 * toggleFavoriteAnalysis/Dashboard). POST /user-config only assigns the keys it
 * receives, so we send just the one list rather than round-tripping the whole
 * config. The cache is updated optimistically and rolled back on failure so the
 * star flips instantly.
 */
export function useToggleFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ kind, id }: { kind: FavoriteKind; id: string }) => {
      const field = FAVORITE_FIELDS[kind];
      const previous = queryClient.getQueryData<UserConfig>(USER_CONFIG_QUERY_KEY);
      const existing = previous?.[field] || [];
      const next = existing.includes(id)
        ? existing.filter((favoriteId) => favoriteId !== id)
        : [...existing, id];

      queryClient.setQueryData<UserConfig>(USER_CONFIG_QUERY_KEY, (old) =>
        old ? { ...old, [field]: next } : old
      );

      try {
        await ApiService.getInstance().request('POST', '/user-config', { [field]: next });
      } catch (error) {
        queryClient.setQueryData<UserConfig>(USER_CONFIG_QUERY_KEY, previous);
        throw error;
      }

      return next;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: USER_CONFIG_QUERY_KEY });
    },
  });
}
